import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Grid,
  Card,
  Avatar,
  Tooltip,
  CircularProgress,
  Pagination
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import EventIcon from "@mui/icons-material/Event";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import CommentIcon from "@mui/icons-material/Comment";
import ReplyIcon from "@mui/icons-material/Reply";
import WorkIcon from "@mui/icons-material/Work";
import StarIcon from "@mui/icons-material/Star";
import PublicIcon from "@mui/icons-material/Public";
import StudentFullProfile from "layouts/profile/components/StudentFullProfile";
import LoadingSkeleton from "components/LoadingSkeleton";
import { adminStyles, loadingConfig } from "styles/adminStyles";
import axiosInstance from "API/axiosInstance";
import { decryptText } from "config/encryption";
import { API_ENDPOINTS, getUploadUrl } from "config/api";

const iconMap = {
  socialMediaCount: <PublicIcon color="info" fontSize="small" />,
  expertiseCount: <StarIcon color="warning" fontSize="small" />,
  chapterCount: <GroupIcon color="primary" fontSize="small" />,
  eventCount: <EventIcon color="success" fontSize="small" />,
  careerPosts: <WorkIcon color="secondary" fontSize="small" />,
  commentCount: <CommentIcon color="info" fontSize="small" />,
  replyCount: <ReplyIcon color="info" fontSize="small" />,
};

const labelMap = {
  socialMediaCount: "Social Media",
  expertiseCount: "Expertise",
  chapterCount: "Chapters",
  eventCount: "Events",
  careerPosts: "Career Posts",
  commentCount: "Comments",
  replyCount: "Replies",
};

const AdminAlumniDetailsDialog = ({ open, onClose }) => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const cardsPerPage = 12;
  const [profileDialog, setProfileDialog] = useState({ open: false, studentId: null });

  // Get token from localStorage for StudentFullProfile component
  const getToken = () => {alert("getToken");
    try {
      const session = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
      if (session) {
        const decrypted = decryptText(session);
        if (decrypted) {
          const parsed = JSON.parse(decrypted);
          return parsed.Token || parsed.token || null;
        }
      }
    } catch (error) {
      console.error("Error extracting token:", error);
    }
    return null;
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axiosInstance.get(API_ENDPOINTS.ADMIN.GET_ALUMNUS_DASHBOARD_DATA)
      .then(res => setAlumni(res.data))
      .catch(() => setAlumni([]))
      .finally(() => setLoading(false));
    setPage(1);
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <Tooltip title="Close">
          <IconButton onClick={onClose} color="error">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <DialogTitle sx={adminStyles.dialogTitle}>Alumni Details</DialogTitle>
      <DialogContent sx={adminStyles.container}>
        {loading ? (
          <LoadingSkeleton variant="grid" rows={12} showHeader={false} />
        ) : (
          <>
            <Grid container spacing={2}>
              {alumni.slice((page-1)*cardsPerPage, page*cardsPerPage).map((alum) => (
                <Grid item xs={12} sm={6} md={2} key={alum.studentId}>
                  <Card sx={{ borderRadius: 2, boxShadow: 2, bgcolor: '#fff', p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 180, maxWidth: 220, mx: 'auto' }}>
                    <Avatar
                      src={alum.img ? getUploadUrl(alum.img) : undefined}
                      sx={{ width: 44, height: 44, mb: 1, bgcolor: 'info.main' }}
                    >
                      {!alum.img && <PersonIcon sx={{ color: '#fff', fontSize: 28 }} />}
                    </Avatar>
                    <Typography variant="body2" fontWeight="bold" color="primary.main" align="center" noWrap sx={{ maxWidth: 90 }}>
                      {alum.fullName}
                    </Typography>
                    <Box mt={1} width="100%">
                      {Object.keys(iconMap).map((key) => (
                        <Box key={key} display="flex" alignItems="center" mb={0.25}>
                          {iconMap[key]}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1, minWidth: 60 }}>
                            {labelMap[key]}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="text.primary" sx={{ ml: 'auto', fontSize: 13 }}>
                            {alum[key]}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={Math.ceil(alumni.length / cardsPerPage)}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="info"
                shape="rounded"
              />
            </Box>
          </>
        )}
      </DialogContent>
      {/* Profile Dialog */}
      <Dialog open={profileDialog.open} onClose={() => setProfileDialog({ open: false, studentId: null })} maxWidth="md" fullWidth>
        <DialogTitle>Student Profile</DialogTitle>
        <DialogContent>
          {profileDialog.studentId && getToken() && (
            <StudentFullProfile studentId={profileDialog.studentId} token={getToken()} />
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

AdminAlumniDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminAlumniDetailsDialog; 